import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableCheckboxCell,
    DataTableColumnHeaderCell,
    DataTableContent,
    DataTableHeaderCheckboxCell,
    DataTableHeaderRow,
    DataTablePagination,
    DataTableRow,
    DataTableToolbar,
    Dialog,
    Search,
    SortDirection,
} from "@episerver/ui-framework";
import { Organization, OrganizationSearchResult, SearchParams, ServerSortType } from "../model/organization";
import { debounce } from "lodash-es";
import { organizationSelector } from "../reducers/organizationDetailReducer";
import axios from "axios";
import { isOpenDialogSelector, onChangeDialog } from "../reducers/addChildOrganizationsReducer";


interface AddChildOrganizationsProps {
    addChildren: ((children: Organization[]) => void) | undefined;
}

const AddChildOrganizations = (props: AddChildOrganizationsProps) => {
    const dispatch = useAppDispatch();
    const organization = useAppSelector(organizationSelector);
    const [defaultSearchParams, setDefaultSearchParams] = useState<SearchParams>({
        query: '',
        sortField: 'name',
        sortType: ServerSortType.Asc,
        startIndex: 0,
        recordsToRetrieve: 25,
    } as SearchParams);
    const [organizations, setOrganizations] = useState<Organization[]>([] as Organization[]);
    const [selectedOrganizations, setSelectedOrganizations] = useState<Organization[]>([] as Organization[]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const open = useAppSelector(isOpenDialogSelector);
    const title = 'Add child organization';
    const mounted = useRef(false);
    const url = window && "https://" +  window.location.host + "/EPiServer/Commerce/";
    const searchOrganizations = async (params: SearchParams): Promise<any> => {
        const res = await axios.get<OrganizationSearchResult>(url + "Organizations/Search", { params });
        return res.data;
    };

    useEffect(() => {
        mounted.current = true;

        searchOrganizations(defaultSearchParams).then((data) => {
            setOrganizations(data.organizations);
            setTotalCount(getTotalRecords(data.organizations, data.totalRecords));
        });

        return () => { mounted.current = false; };
    }, []);

    const getTotalRecords = function (organizations: Organization[], totalRecords: any) {
        if (organizations.filter((org: Organization) => org.primaryKeyId == organization.primaryKeyId).length > 0) {
            return totalRecords -1;
        }
        return totalRecords;
    };

    const columns: Array<{ selector: string; title: string; sortable: boolean }> = [
        { selector: "Name", title: "Name", sortable: true },
        { selector: "PayerId", title: "Payer Id", sortable: true },
    ];

    const onSelectedRowChanged = (row: Organization, checked: boolean): void => {
        let newSelectedArray = [] as Organization[];
        Object.assign(newSelectedArray, selectedOrganizations);
        if (checked) {
            newSelectedArray.push(row);
        }
        else {
            const index = selectedOrganizations.indexOf(row);
            if (index > -1) { // only splice array when item is found
                newSelectedArray.splice(index, 1); // 2nd parameter means remove one item only
            }
        }
        setSelectedOrganizations(newSelectedArray);
    };

    const handleSearch = (value: string) => debouncedSearch(value);
    const debouncedSearch = useCallback(debounce((nextValue: string) => onSearch(nextValue), 500), [],);
    const onSearch = (value: string): void => {
        const params = defaultSearchParams;
        params.query = value;
        searchOrganizations(params).then((data) => {
            setDefaultSearchParams(params);
            setOrganizations(data.organizations);
            setTotalCount(getTotalRecords(data.organizations, data.totalRecords));
        });
    };

    const onToggleAllSelected = (checked: boolean): void => {
        const newSelectedArray = [] as Organization[];
        if (checked) {
            for(var i = 0; i < organizations.length; i++) {
                newSelectedArray.push(organizations[i]);
            }
        }
        setSelectedOrganizations(newSelectedArray);
    };

    const onSort = (column: string, direction: SortDirection): void => {
        const params = defaultSearchParams;
        params.sortField = column;
        params.sortType = direction === SortDirection.Ascending || SortDirection.Unsorted ? ServerSortType.Asc : ServerSortType.Desc;
        searchOrganizations(params).then((data) => {
            setDefaultSearchParams(params);
            setOrganizations(data.organizations);
            setTotalCount(getTotalRecords(data.organizations, data.totalRecords));
        });
    };

    const isSelected = (row: Organization): boolean => {
        return organization.children.find(x => x.primaryKeyId == row.primaryKeyId) != null;
    };

    const onSubmit = async (): Promise<void> => {
        if (props.addChildren) {
            props.addChildren(selectedOrganizations);
        }
        setSelectedOrganizations([]);
        dispatch(onChangeDialog({ open: false }));
    };

    const renderRow = (rowData: Organization): React.ReactElement => {
        return (
            <DataTableRow
                rowId={rowData.primaryKeyId!}
                key={rowData.primaryKeyId}
                selected={isSelected(rowData)}
            >
                <DataTableCheckboxCell
                    defaultChecked={isSelected(rowData)}
                    onChange={(e) => onSelectedRowChanged(rowData, e.target.checked)}
                />
                <DataTableCell>{rowData.name}</DataTableCell>
                <DataTableCell>{rowData.extendedProperties?.find(property => property.name === "PayerId")?.value ?? ""}</DataTableCell>
            </DataTableRow>
        );
    };

    return (
        <Dialog
            className="child-organization__add-form"
            open={open}
            title={title}
            dismissLabel="Cancel"
            confirmLabel="Add"
            enableConfirm={selectedOrganizations.length > 0}
            onInteraction={(evt) => {
                if (evt.detail.action === "confirm") {
                    onSubmit();
                } else {
                    dispatch(onChangeDialog({ open: false }));
                }
            }}
        >
            <div className="organization-list">
                <DataTable>
                    <DataTableToolbar className="organization-list__toolbar">
                    <Search onValueChange={handleSearch} onSearch={handleSearch} value={defaultSearchParams.query} />
                    </DataTableToolbar>
                    <DataTableContent>
                        <DataTableHeaderRow>
                            <Fragment>
                                {organizations.length > 0 && (
                                    <DataTableHeaderCheckboxCell
                                        onChange={(e) => onToggleAllSelected(e.target.checked)}
                                    />
                                )}
                            </Fragment>
                            <React.Fragment>
                                {columns.map((column) => {
                                    if (column.sortable)
                                        return (
                                            <DataTableColumnHeaderCell
                                                key={column.selector}
                                                onSort={(_header: string, direction: SortDirection) =>
                                                    onSort(column.selector, direction)
                                                }
                                                sortDirection={
                                                    defaultSearchParams.sortField !== column.selector ?  SortDirection.Ascending :
                                                    defaultSearchParams.sortType === ServerSortType.Asc
                                                        ? SortDirection.Ascending
                                                        : SortDirection.Descending
                                                }
                                            >
                                                {column.title}
                                            </DataTableColumnHeaderCell>
                                        );
                                    return (
                                        <DataTableColumnHeaderCell key={column.selector}>
                                            {column.title}
                                        </DataTableColumnHeaderCell>
                                    );
                                })}
                            </React.Fragment>
                        </DataTableHeaderRow>
                        <DataTableBody>
                            {organizations
                            .filter((row: Organization) => organization.primaryKeyId != row.primaryKeyId)
                            .map((row: Organization) => {
                                return renderRow(row);
                            })}
                        </DataTableBody>
                    </DataTableContent>
                    <DataTablePagination
                        startRow={defaultSearchParams.startIndex}
                        totalRowCount={totalCount}
                        rowsPerPage={defaultSearchParams.recordsToRetrieve}
                        rowsPerPageOptions={[25, 50, 100]}
                        onChangePage={(startRow: number) => {
                            const params = defaultSearchParams;
                            params.startIndex = startRow === 0 ? 0 : startRow;
                            searchOrganizations(params).then((data) => {
                                setDefaultSearchParams(params);
                                setOrganizations(data.organizations);
                                setTotalCount(getTotalRecords(data.organizations, data.totalRecords));
                            });
                        }}
                        onChangeRowsPerPage={(rowsPerPage: number) => {
                            const params = defaultSearchParams;
                            params.startIndex = 0;
                            params.recordsToRetrieve = rowsPerPage;
                            searchOrganizations(params).then((data) => {
                                setDefaultSearchParams(params);
                                setOrganizations(data.organizations);
                                setTotalCount(getTotalRecords(data.organizations, data.totalRecords));
                            });
                        }}
                    />
                </DataTable>
            </div>
        </Dialog>
    );
};

export default AddChildOrganizations;